/** Painting the app shell in an organisation's brand colour (POS2-1132).
 *
 * **Why these five variables and not `--color-primary` directly.** `app.css` declares its
 * palette inside `@theme inline`, which inlines each value into the generated utilities rather
 * than emitting `var(--color-primary)`. So `bg-primary` compiles to the literal colour, and
 * writing `--color-primary` on `documentElement` at runtime changes precisely nothing. The
 * theme block instead points those five tokens at `--brand-*` custom properties declared in
 * `:root`, which is the case `inline` exists for — and it is why the acceptance test for this
 * is `getComputedStyle` in a real browser, not a spy on `setProperty`.
 *
 * **Five variables rather than two**, even though only two colours are involved: each token
 * keeps its own unbranded default in `:root`, and those defaults differ. `--color-ring` is a
 * mid grey where `--color-primary` is near-black, so collapsing them would change every focus
 * ring in the app for every organisation that has no brand colour at all.
 *
 * **What is deliberately not branded:** charts, destructive, muted, border, background and the
 * waste-tier palette. Those are semantic — a brand-coloured "critical" badge is a defect, not a
 * feature.
 */

/** `organisations.primary_color` has defaulted to this since POS2-102, so it means "nobody
 * chose a colour" rather than "this organisation is navy". Applying it would repaint every
 * unbranded organisation's buttons from neutral near-black to navy, which is exactly what the
 * acceptance criterion "renders identically to today" rules out. */
export const PLATFORM_DEFAULT_COLOR = "#0f172a";

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

/** A well-formed six-digit hex, whatever colour it names.
 *
 * Deliberately not `isBrandColor`: that one also rejects the platform default, which is right for
 * "should the shell be repainted" and wrong for "may this be shown in a swatch" — the default is a
 * real colour a picker has to display. One home for the pattern, since the branding editor used to
 * carry its own identical copy. */
export function isHexColor(value: string | null | undefined): value is string {
  return !!value && HEX_COLOR.test(value);
}

/** Above this OKLab lightness a colour needs dark text on it rather than light. 0.65 sits
 * between the two cases that matter — mid-blues and greens read as dark, yellows and pale
 * pastels as light — and both sides land well clear of the 4.5:1 contrast floor. */
const LIGHT_COLOR_THRESHOLD = 0.65;

const DARK_FOREGROUND = "oklch(0.205 0 0)";
const LIGHT_FOREGROUND = "oklch(0.985 0 0)";

/** The five tokens whitelabel drives, and the two values that fill them. */
const BRAND_VARIABLES = ["--brand-primary", "--brand-ring", "--brand-sidebar-primary"] as const;
const BRAND_FOREGROUND_VARIABLES = [
  "--brand-primary-foreground",
  "--brand-sidebar-primary-foreground",
] as const;

export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/** A brand colour worth applying: set, well-formed, and not the platform default. */
export function isBrandColor(hex: string | null | undefined): hex is string {
  return !!hex && HEX_COLOR.test(hex) && hex.toLowerCase() !== PLATFORM_DEFAULT_COLOR.toLowerCase();
}

function srgbToLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

/** sRGB hex → OKLCH, by Björn Ottosson's matrices.
 *
 * OKLCH rather than HSL because the app's whole palette is already in it, and because `l` is a
 * perceptual lightness — which is what makes the foreground choice below a two-line rule
 * instead of a luminance heuristic.
 */
export function hexToOklch(hex: string): Oklch {
  const r = srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255);
  const g = srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255);
  const b = srgbToLinear(parseInt(hex.slice(5, 7), 16) / 255);

  const lCone = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const mCone = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const sCone = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const l = 0.2104542553 * lCone + 0.793617785 * mCone - 0.0040720468 * sCone;
  const a = 1.9779984951 * lCone - 2.428592205 * mCone + 0.4505937099 * sCone;
  const bAxis = 0.0259040371 * lCone + 0.7827717662 * mCone - 0.808675766 * sCone;

  const c = Math.sqrt(a * a + bAxis * bAxis);
  // A grey has no meaningful hue; reporting 0 keeps the emitted CSS stable rather than letting
  // floating-point noise in `atan2(0, 0)` pick an angle.
  const h = c < 1e-6 ? 0 : ((Math.atan2(bAxis, a) * 180) / Math.PI + 360) % 360;

  return { l, c, h };
}

export function oklchToCss({ l, c, h }: Oklch): string {
  return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(2)})`;
}

/** Dark text on a light brand colour, light text on a dark one. */
export function foregroundFor({ l }: Oklch): string {
  return l > LIGHT_COLOR_THRESHOLD ? DARK_FOREGROUND : LIGHT_FOREGROUND;
}

/** Paint the five brand tokens, or restore the FoodSight palette when there is no colour.
 *
 * Written as inline properties on `documentElement`, which outrank `:root` in the cascade, so
 * clearing is `removeProperty` and the stylesheet's defaults come back on their own.
 */
export function applyBrand(hex: string | null | undefined): void {
  const root = document.documentElement;
  if (!isBrandColor(hex)) {
    clearBrand();
    return;
  }
  const colour = hexToOklch(hex);
  const value = oklchToCss(colour);
  const foreground = foregroundFor(colour);
  for (const variable of BRAND_VARIABLES) {
    root.style.setProperty(variable, value);
  }
  for (const variable of BRAND_FOREGROUND_VARIABLES) {
    root.style.setProperty(variable, foreground);
  }
}

export function clearBrand(): void {
  const root = document.documentElement;
  for (const variable of [...BRAND_VARIABLES, ...BRAND_FOREGROUND_VARIABLES]) {
    root.style.removeProperty(variable);
  }
}
